import ReactJson from "@microlink/react-json-view";
import { Spin, Tag } from "antd";
import moment from "moment-timezone";
import { Link } from "react-router-dom";
import { REACT_APP_REDIRECT_URL } from "../constants";
import { showServerError, showSessionError } from "./notificationService";
import { logoutHandler } from "./navigateHandler";

export const isAuthenticated = () => {
  return !!localStorage.getItem("token"); // or cookie / store
};

// localStorage helpers to getData
export const getLocalStorageData = (key) => {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error(`Error parsing localStorage key "${key}":`, error);
    return null;
  }
};

// localStorage helpers to setData
export const setLocalStorageData = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Error setting localStorage key "${key}":`, error);
  }
};

export const apiGetSuccess = ({ status }) => {
  return status === 200;
};

export const apiBadRequest = ({ status }) => {
  return status === 404;
};

export const apiServerError = ({ status }) => {
  return status === 500;
};

export function isEmpty(value) {
  return value === null || value === undefined || (typeof value === "string" && value.trim() === "") || (Array.isArray(value) && value.length === 0) || (typeof value === "object" && Object.keys(value).length === 0);
}

export const findUserType = (userItem) => {
  if (userItem?.userType?.toLowerCase() === "serviceaccount") return "Service Account";
  if (userItem?.userType?.toLowerCase() === "entrauser") return "Service Account";
  if (userItem?.userType?.toLowerCase() === "normaluser") return "User";
};

export function checkAPISuccess(data) {
  return data && (data.Code === 200 || data.code === 200 || data.code === 201 || data.status === 200);
}

export const fetchBaseUrl = () => {
  // window.location.protocol typically returns "http:" or "https:"
  const protocol = window.location.protocol;
  // window.location.hostname typically returns "localhost" or "www.example.com"
  const hostname = window.location.hostname;
  // window.location.port typically returns "3001" (if specified) or ""
  const port = window.location.port;
  // Combine them into the base URL string
  // We use a ternary operator to correctly format the URL whether a port exists or not
  const baseURL = port ? `${protocol}//${hostname}:${port}` : `${protocol}//${hostname}`;
  return baseURL;
};

export const fetchBaseDomain = () => {
  // window.location.hostname typically returns "localhost" or "www.example.com"
  const hostname = window.location.hostname;
  // window.location.port typically returns "3001" (if specified) or ""
  const port = window.location.port;
  // Combine them into the base URL string
  // We use a ternary operator to correctly format the URL whether a port exists or not
  const baseURL = port ? `${hostname}:${port}` : `${hostname}`;
  return baseURL;
};

export function getBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file); // this gives data:image/jpeg;base64,xxxx
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

export const setLocalStorage = (key, value) => {
  return localStorage.setItem(key, value);
};

export const getLocalStorage = (key) => {
  return localStorage.getItem(key) || null;
};

export const formatTimeByTimezone = (time, timezone) => {
  return moment(time).tz(timezone).format("YYYY-MM-DD HH:mm z");
};

export const AUTH_TOKEN = () => {
  return `${getToken()}&DOMAIN&${REACT_APP_REDIRECT_URL}&ORGID&${ORG_ID()}`;
};

export const AUTH_TOKEN_ORG = () => {
  return `${getToken()}&DOMAIN&${REACT_APP_REDIRECT_URL}&ORGID&${ORG_ID()}&ORGLOGIN&`;
};

export const AUTH_TOKEN_ORG_LOGIN = () => {
  const token = getToken();
  const domainUrl = REACT_APP_REDIRECT_URL;
  return `${token}&DOMAIN&${domainUrl}&ORGLOGIN&`;
};

export const AUTH_TOKEN_DOMAIN = () => {
  const token = getToken();
  const domainUrl = REACT_APP_REDIRECT_URL;
  const AUTHORIZATION = `${token}&DOMAIN&${domainUrl}`;
  return AUTHORIZATION;
};

export const getUserName = () => {
  return localStorage.getItem("UserName") || null;
};

export const getUserRole = () => {
  return localStorage.getItem("UserRole") || null;
};

// Admin and superadmin may change PAM settings; the backend enforces it too.
export const isAdminUser = () => ["admin", "superadmin"].includes(String(getUserRole() || "").toLowerCase());

export const getToken = () => {
  return localStorage.getItem("token") || null;
};

export const ORG_ID = () => {
  let OrgId = localStorage.getItem("OrgId");
  return Number(OrgId);
};

export const DOMAIN_ID = () => {
  let OrgId = localStorage.getItem("DomainId");
  return Number(OrgId);
};

export const setDomainUrl = () => {
  // On-prem: the tenant is identified by DOMAIN_URL, not the browser host. On a
  // bare-IP install the host is the VM IP, which the backend can't map to an org.
  const domainUrl = REACT_APP_REDIRECT_URL || window.location.host;
  return localStorage.setItem("URL", domainUrl);
};

export function getDomainUrl() {
  return localStorage.getItem("URL") || null;
}

export function getRequestId() {
  return localStorage.getItem("requestId") || null;
}

export const CustomTagRender = ({ value, isCap = false, handleOnclick = () => {}, color = "blue" }) => {
  return (
    <Tag onClick={handleOnclick} color={color} className={`cursor-pointer`}>
      <span className={`text-xs  ${isCap ? "!capitalize" : ""}`}>{value}</span>
    </Tag>
  );
};

export const CustomLinkRender = ({ value, isCap = false, handleOnclick = () => {}, color = "blue" }) => {
  return (
    <Link onClick={handleOnclick} color={color} className={`cursor-pointer underline text-primary`}>
      <span className={`text-xs  ${isCap ? "!capitalize" : ""}`}>{value}</span>
    </Link>
  );
};

export const JsonCellRenderer = ({ value }) => <ReactJson src={value} name={false} theme={"rjv-default"} enableClipboard={false} enableEdit={false} enableAdd={false} enableDelete={false} displayDataTypes={false} displayObjectSize={false} displayArrayKey={false} />;

export const manageServiceError = (error, onFailure, title) => {
  const { data } = error.response || {};
  const isSessionError = data?.message === "Invalid credentials" || error?.response?.status === 401;
  if (data?.code === 500) {
    showServerError(title);
  }
  if (isSessionError) {
    logoutHandler();
    showSessionError(title);
  }
  onFailure && onFailure(error.response);
  return;
};

export const Spinner = () => {
  return (
    <div className="text-center mt-4">
      <Spin tip="Loading" className="flex items-center justify-center" />
    </div>
  );
};

export function findOpenKeyFromPath(pathsToCheck, items) {
  // A copy: reversing in place reordered the caller's list, which then reached
  // findSelectedKeyFromPath already reversed.
  for (const path of [...pathsToCheck].reverse()) {
    for (const item of items) {
      if (item.children?.some((child) => child.key === path)) {
        return item.key;
      }
    }
  }
  return null;
}

export function findSelectedKeyFromPath(pathsToCheck, items) {
  // Longest path first, so /pam/radius/servers selects "RADIUS Servers", not its parent
  // "Network Devices" (/pam/radius). OLD: for (const path of pathsToCheck) {
  for (const path of [...pathsToCheck].reverse()) {
    for (const item of items) {
      if (item.key === path) return item.key;
      if (item.children) {
        for (const child of item.children) {
          if (child.navigate === path) {
            return child.key;
          }
        }
      }
    }
  }

  return null;
}

export const isOrgAccess = () => {
  return (window.location.host.includes("localhost") || window.location.host.includes("app.authnull.com")) && getUserRole() === "SUPERADMIN";
};

export const generateDynamicPolicyName = (prefix, length) => {
  const characters = "0123456789";
  let result = prefix;
  const sections = [7, 8];
  for (let sectionLength of sections) {
    for (let i = 0; i < sectionLength; i++) {
      const randomCharIndex = Math.floor(Math.random() * characters.length);
      result += characters.charAt(randomCharIndex);
    }
    if (sectionLength !== 8) {
      result += "-";
    }
  }

  return result;
};
